import { Component, OnInit, ViewChild } from '@angular/core';
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule, MatSelect } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { ActivatedRoute, Router } from '@angular/router';
import { ReservationService } from '@services/reservation.service';
import { PropertyService, type Property } from '@services/property.service';
import { AppStateService } from '@services/app-state.service';
import type { ValamarBooking } from '@models/valamar-guest.interface';
import { Location } from '@angular/common';
import { GuestPackageType } from '@app/utils';
@Component({
  selector: 'app-guest-verify',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSlideToggle,
    MatButtonToggleModule
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './guest-verify.component.html',
  styleUrls: ['./guest-verify.component.scss']
})
export class GuestVerifyComponent implements OnInit {
  @ViewChild('hotelSelect') hotelSelect!: MatSelect;
  isValamarGuest: boolean = false;
  loading: boolean = false;
  error: string | null = null;

  // Search form
  propertyCode: string = 'PHPIC';
  roomNumber: string = '';
  lastName: string = '';

  // Results
  searchResults: ValamarBooking[] = [];
  selectedBooking: ValamarBooking | null = null;

  // Properties
  properties: Property[] = [];
  filteredProperties: Property[] = [];
  propertySearchTerm: string = '';

  // Package selection (V-Level override)
  selectedPackage: GuestPackageType = GuestPackageType.EXTERNAL;
  vLevel: boolean = false;

  constructor(
    private reservationService: ReservationService,
    private propertyService: PropertyService,
    private appState: AppStateService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location
  ) {}

  ngOnInit(): void {
    // Load existing state
    this.isValamarGuest = this.appState.isValamarGuest;
    this.selectedBooking = this.appState.valamarBooking;
    this.vLevel = this.appState.guestPackage == GuestPackageType.VLEVEL;
    // Initialize package selection based on existing booking
    this.selectedPackage = this.appState.guestPackage

    // Load properties
    this.propertyService.getProperties().subscribe(properties => {
      this.properties = properties;
      this.filteredProperties = properties;
    });
  }

  onValamarGuestChange(event: MatSlideToggleChange | boolean): void {
    let checked: boolean;
    if (typeof event === 'boolean') {
      checked = event;
    } else if (event && typeof event === 'object' && 'checked' in event) {
      checked = (event as any).checked;
    } else {
      checked = this.isValamarGuest; // fallback to current value
    }
    this.isValamarGuest = checked;
    this.searchResults = [];
    this.selectedBooking = null;
    this.error = null;
  }

  onSearch(): void {
    if (!this.propertyCode || !this.roomNumber) {
      this.error = 'Molimo ispunite sva polja.';
      return;
    }

    this.loading = true;
    this.error = null;
    this.searchResults = [];
    this.selectedBooking = null; // Clear any previous selection

    // Convert dates to YYYY-MM-DD format

    this.reservationService.searchValamarBookings({
      propertyCode: this.propertyCode,
      roomNumber: this.roomNumber
    }).subscribe({
      next: (results) => {
        this.searchResults = results;
        this.loading = false;

        if (results.length === 0) {
          this.error = 'Nema pronađenih rezervacija.';
        }
        // Note: Removed auto-selection for single result - operator must manually confirm
      },
      error: (err) => {
        this.error = err.message || 'Greška prilikom pretrage.';
        this.loading = false;
      }
    });
  }

  selectBooking(booking: ValamarBooking): void {
    this.selectedBooking = booking;
    this.error = null;

    // Initialize package selection based on booking V-Level status
    this.selectedPackage = booking.vlevel ? GuestPackageType.VLEVEL : GuestPackageType.COLLECTION;
    // Reset override when selecting new booking
    this.appState.setGuestPackage(this.selectedPackage);
  }

  onConfirm(): void {
    this.appState.setIsValamarGuest(this.isValamarGuest);
    if (this.isValamarGuest && this.selectedBooking) {
      this.appState.setValamarBooking(this.selectedBooking);
    }
    this.appState.setCurrentStep(3);
    this.router.navigate(['/credit']);
  }

  onCancelSelection(): void {
    this.selectedBooking = null;
    this.appState.setValamarBooking(null);
  }

  onBack(): void {
    this.appState.setCurrentStep(1);
    this.location.back();
  }

  onPackageChange(newGuestPackage: GuestPackageType): void {
    this.selectedPackage = newGuestPackage;
    // Only set override if different from original booking status
    const originalPackage = this.selectedPackage;
    this.appState.setGuestPackage(newGuestPackage);
  }

  get canContinue(): boolean {
    if (!this.isValamarGuest) {
      return true; // Non-Valamar guests can continue
    }
    return this.selectedBooking !== null;
  }

  get bookerName(): string {
    const booking = this.selectedBooking;
    if (!booking || !booking.accountData) return '';

    const accountData = booking.accountData;
    return `${accountData.firstName || ''} ${accountData.lastName || ''}`.trim();
  }

  onClear(): void {
    this.propertyCode = '';
    this.roomNumber = '';
    this.lastName = '';
    this.propertySearchTerm = '';
    this.filteredProperties = this.properties;
    this.searchResults = [];
    this.selectedBooking = null;
    this.error = null;
  }

  filterProperties(): void {
    const searchTerm = this.propertySearchTerm.toLowerCase().trim();
    if (!searchTerm) {
      this.filteredProperties = this.properties;
    } else {
      this.filteredProperties = this.properties.filter(prop =>
        prop.name.toLowerCase().includes(searchTerm)
      );
    }
  }
}
